import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { MODULE_TYPES, Module, FOTBOLLPLAY_CATEGORIES, FotbollPlayChannel } from './types';
import { FIELD_TYPES } from '../../../types';
import svStrings from '../../../sv-strings';
import { PROMO_VARIANT } from '../../../types/team';

const fotbollPlayCategories = Object.values(FOTBOLLPLAY_CATEGORIES);
const translatedFotbollPlayTypes = fotbollPlayCategories.map(
  (category) => svStrings.fotbollPlayCategories[category]
);

const getSceneModules = (fotbollConfigChannels?: FotbollPlayChannel[] | null): Module[] => {
  const modules = [
    {
      type: MODULE_TYPES.EDITORIAL,
      name: svStrings.sceneModules.editorial,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'standings-table-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.FOTBOLLPLAY_VIDEOS,
      name: svStrings.sceneModules.fotbollPlayVideos,
      id: uuidv4(),
      fields: [
        {
          key: 'videoChannels',
          value: [],
          id: 'fotbollplay-video-channels',
          title: svStrings.sceneFields.channels,
          type: FIELD_TYPES.MULTI_SELECT,
          options: fotbollConfigChannels?.map((channel) => channel.id) || [],
          optionsText: fotbollConfigChannels?.map((channel) => channel.title) || [],
          isReadOnly: !(fotbollConfigChannels && fotbollConfigChannels.length > 1) || false,
          validation: yup
            .number()
            .oneOf(
              fotbollConfigChannels?.map((channel) => channel.id) || [],
              svStrings.validations.mustSelectAlternative
            ),
        },
        {
          key: 'videoCategory',
          value: '',
          id: 'fotbollplay-video-category',
          title: svStrings.sceneFields.category,
          type: FIELD_TYPES.SELECT,
          options: fotbollPlayCategories,
          optionsText: translatedFotbollPlayTypes,
          isReadOnly: !(fotbollConfigChannels && fotbollConfigChannels.length > 1) || false,
          validation: yup
            .string()
            .oneOf(fotbollPlayCategories, svStrings.validations.mustSelectAlternative),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'links-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.STORIES,
      name: svStrings.sceneModules.stories,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'stories-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.PROMO_IMAGES,
      name: svStrings.sceneModules.promoImages,
      id: uuidv4(),
      fields: [
        {
          key: 'promoIndexes',
          value: [],
          id: 'promo-indexes',
          title: svStrings.sceneFields.selectPromoMultiple,
          type: FIELD_TYPES.PROMO_SELECT_MULTIPLE,
          optionsFilter: PROMO_VARIANT.IMAGE,
          options: Array.from(Array(18)).map((_, index) => index.toString()),
          validation: yup.string(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'promosTimed',
          value: null,
          id: 'promos-timed',
          title: svStrings.sceneFields.isTimed,
          type: FIELD_TYPES.PROMO_TIMED,
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.PROMO_VIDEO,
      name: svStrings.sceneModules.promoVideo,
      id: uuidv4(),
      fields: [
        {
          key: 'promoIndex',
          value: '',
          id: 'promo-index',
          title: svStrings.sceneFields.selectPromo,
          type: FIELD_TYPES.PROMO_SELECT,
          optionsFilter: PROMO_VARIANT.FORZASYS_VIDEO,
          options: Array.from(Array(18)).map((_, index) => index.toString()),
          validation: yup.string(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.PROMO_POLL,
      name: svStrings.sceneModules.promoPoll,
      id: uuidv4(),
      fields: [
        {
          key: 'promoIndex',
          value: '',
          id: 'promo-index',
          title: svStrings.sceneFields.selectPromo,
          type: FIELD_TYPES.PROMO_SELECT,
          optionsFilter: PROMO_VARIANT.POLL,
          options: Array.from(Array(18)).map((_, index) => index.toString()),
          validation: yup.string(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'promosTimed',
          value: null,
          id: 'promos-timed',
          title: svStrings.sceneFields.isTimed,
          type: FIELD_TYPES.PROMO_TIMED,
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.PROMO_SUBSCRIPTION,
      name: svStrings.sceneModules.promoSubscription,
      id: uuidv4(),
      fields: [
        {
          key: 'promoIndex',
          value: '',
          id: 'promo-index',
          title: svStrings.sceneFields.selectPromo,
          type: FIELD_TYPES.PROMO_SELECT,
          optionsFilter: PROMO_VARIANT.SUBSCRIPTION,
          options: Array.from(Array(18)).map((_, index) => index.toString()),
          validation: yup.string(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.PROMO_FLARIE,
      name: svStrings.sceneModules.promoFlarie,
      id: uuidv4(),
      fields: [
        {
          key: 'promoIndexes',
          value: [],
          id: 'promo-indexes',
          title: svStrings.sceneFields.selectPromoMultiple,
          type: FIELD_TYPES.PROMO_SELECT_MULTIPLE,
          optionsFilter: PROMO_VARIANT.FLARIE,
          options: Array.from(Array(18)).map((_, index) => index.toString()),
          validation: yup.string(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'promosTimed',
          value: null,
          id: 'promos-timed',
          title: svStrings.sceneFields.isTimed,
          type: FIELD_TYPES.PROMO_TIMED,
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.UNIBET_PROMO_WEBVIEW,
      name: svStrings.sceneModules.unibetPromoWebview,
      isNonRemoveable: true,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'unibet-promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          validation: yup.boolean(),
        },
        {
          key: 'open_inapp',
          value: '',
          id: 'unibet-open-in-app',
          title: svStrings.sceneFields.openInApp,
          type: FIELD_TYPES.SWITCH,
          validation: yup.bool(),
        },
        {
          key: 'url',
          value: '',
          id: 'unibet-url',
          title: svStrings.sceneFields.url,
          type: FIELD_TYPES.URL,
          validation: yup.string().url(svStrings.validations.validUrl),
        },
        {
          key: 'aspect_ratio',
          value: 0,
          id: 'unibet-aspect-ratio',
          title: svStrings.sceneFields.aspectRatio,
          type: FIELD_TYPES.NUMBER,
          validation: yup.number().nullable().positive(svStrings.validations.mustBePositive),
        },
      ],
    },
    {
      type: MODULE_TYPES.SVENSKASPEL_PROMO,
      name: svStrings.sceneModules.svenskaSpelPromo,
      isNonRemoveable: true,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'svenskaspel-promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          validation: yup.boolean(),
        },
        {
          key: 'adId',
          value: '',
          id: 'svenskaspel-adId',
          title: svStrings.sceneFields.adId,
          type: FIELD_TYPES.TEXT,
          validation: yup.string(),
        },
        {
          key: 'dId',
          value: '',
          id: 'svenskaspel-did',
          title: svStrings.sceneFields.dId,
          type: FIELD_TYPES.TEXT,
          validation: yup.string(),
        },
        {
          key: 'redirectUrl',
          value: '',
          id: 'svenskaspel-redirect-url',
          title: svStrings.sceneFields.redirectUrl,
          type: FIELD_TYPES.URL,
          validation: yup.string().url(svStrings.validations.validUrl),
        },
        {
          key: 'width',
          value: 0,
          id: 'svenskaspel-width',
          title: svStrings.sceneFields.width,
          type: FIELD_TYPES.NUMBER,
          validation: yup
            .number()
            .nullable()
            .integer(svStrings.validations.mustBeInteger)
            .positive(svStrings.validations.mustBePositive),
        },
        {
          key: 'height',
          value: 0,
          id: 'svenskaspel-height',
          title: svStrings.sceneFields.height,
          type: FIELD_TYPES.NUMBER,
          validation: yup
            .number()
            .nullable()
            .integer(svStrings.validations.mustBeInteger)
            .positive(svStrings.validations.mustBePositive),
        },
      ],
    },
    {
      type: MODULE_TYPES.STANDINGS_TABLE,
      name: svStrings.sceneModules.standingsTable,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'standings-table-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.TEAM_PLAYERS_BANNER,
      name: svStrings.sceneModules.teamPlayersBanner,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'team-players-banner-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.TEAM_PLAYERS,
      name: svStrings.sceneModules.teamPlayers,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'team-players-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    // Disabled with Antourage
    // {
    //  type: MODULE_TYPES.CLUB_STREAMS_ARCHIVE,
    //  name: svStrings.sceneModules.clubStreamsArchive,
    //  id: uuidv4(),
    //  fields: [
    //    {
    //      key: 'geo_restricted',
    //      value: false,
    //      id: 'club-streams-archive-geo-restricted',
    //      title: svStrings.sceneFields.geoRestricted,
    //      type: FIELD_TYPES.SWITCH,
    //      options: [],
    //      validation: yup.boolean(),
    //    },
    //  ],
    // },
    {
      type: MODULE_TYPES.NOTIFICATION_CARD,
      name: svStrings.sceneModules.notificationCard,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'notification-card-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.SIDE_SCROLLING_NEWS,
      name: svStrings.sceneModules.sideScrollingNews,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'side-scrolling-news-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.SIDE_SCROLLING_TEAMS,
      name: svStrings.sceneModules.sideScrollingTeams,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'side-scrolling-teams-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.MATCH_CARD,
      name: svStrings.sceneModules.matchCard,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'match-card-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.MATCH_LIST_CARD,
      name: svStrings.sceneModules.matchListCard,
      id: uuidv4(),
      fields: [
        {
          key: 'showMatchListButton',
          value: false,
          id: 'show-match-list-button',
          title: svStrings.sceneFields.matchListButton,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'showTicketButton',
          value: false,
          id: 'show-ticket-button',
          title: svStrings.sceneFields.ticketButton,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'match-list-card-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.LEADER_TABLE_CARD,
      name: svStrings.sceneModules.leaderTableCard,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'leader-table-card-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'limit',
          value: 5,
          id: 'leader-limit',
          title: svStrings.sceneFields.listLimit,
          type: FIELD_TYPES.NUMBER,
          options: [],
          validation: yup
            .number()
            .nullable()
            .integer(svStrings.validations.mustBeInteger)
            .positive(svStrings.validations.mustBePositive),
        },
        {
          key: 'tableType',
          value: '',
          id: 'leader-table-type',
          title: svStrings.sceneFields.tableType,
          type: FIELD_TYPES.SELECT,
          options: ['goals', 'assists', 'goals_and_assists'],
          optionsText: [
            svStrings.sceneFields.goals,
            svStrings.sceneFields.assists,
            svStrings.sceneFields.goalsAndAssists,
          ],
          validation: yup
            .string()
            .oneOf(
              ['goals', 'assists', 'goals_and_assists'],
              svStrings.validations.mustBeFollowingValues + ' goals, assists, goals_and_assists'
            ),
        },
      ],
    },
    // Disabled with Antourage
    // {
    //  type: MODULE_TYPES.CLUB_STREAMS,
    //  name: svStrings.sceneModules.clubStreams,
    //  id: uuidv4(),
    //  fields: [
    //    {
    //      key: 'geo_restricted',
    //      value: false,
    //      id: 'club-streams-geo-restricted',
    //      title: svStrings.sceneFields.geoRestricted,
    //      type: FIELD_TYPES.SWITCH,
    //      options: [],
    //      validation: yup.boolean(),
    //    },
    //  ],
    // },
    {
      type: MODULE_TYPES.APP_PROMO,
      name: svStrings.sceneModules.appPromo,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'app-promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.APP_INFO,
      name: svStrings.sceneModules.appInfo,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'app-info-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
        {
          key: 'privacyPolicyUrl',
          value: '',
          id: 'app-info-privacy-policy-url',
          title: 'Privacy policy url',
          type: FIELD_TYPES.URL,
          options: [],
          validation: yup.string().url(),
        },
        {
          key: 'termsAndConditionUrl',
          value: '',
          id: 'app-info-terms-condition-url',
          title: 'Terms condition url',
          type: FIELD_TYPES.URL,
          options: [],
          validation: yup.string().url(),
        },
        {
          key: 'contestsRulesUrl',
          value: '',
          id: 'app-info-contests-rules-url',
          title: 'Contests rules url',
          type: FIELD_TYPES.URL,
          options: [],
          validation: yup.string().url(),
        },
      ],
    },
    {
      type: MODULE_TYPES.WIFI_CARD,
      name: svStrings.sceneModules.wifiCard,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'wifi-card-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.LINKS,
      name: svStrings.sceneModules.links,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'links-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.LATEST_HIGHLIGHTS_PLUS_VIDEOS,
      name: svStrings.sceneModules.latestHighlightsPlusVideos,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'links-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    // TODO Remove old promo stuff (when removed from db)
    {
      type: MODULE_TYPES.PROMO,
      name: svStrings.sceneModules.promo,
      id: uuidv4(),
      fields: [
        {
          key: 'promoIndex',
          value: '',
          id: 'promo-index',
          title: svStrings.sceneFields.selectPromo,
          type: FIELD_TYPES.PROMO_SELECT,
          options: Array.from(Array(18)).map((_, index) => index.toString()),
          validation: yup.string(),
        },
        {
          key: 'geo_restricted',
          value: false,
          id: 'promo-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
    {
      type: MODULE_TYPES.OFFICIAL_HIGHLIGHTS_REELS,
      name: svStrings.sceneModules.officialHighlightsReels,
      id: uuidv4(),
      fields: [
        {
          key: 'geo_restricted',
          value: false,
          id: 'links-geo-restricted',
          title: svStrings.sceneFields.geoRestricted,
          type: FIELD_TYPES.SWITCH,
          options: [],
          validation: yup.boolean(),
        },
      ],
    },
  ];

  return modules;
};

export default getSceneModules;
